import type { paths } from "@/api-schema";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { handlePaginatedParameters } from "../_helpers/handle-parameters";
import { optionsWithDefaultsPaginated } from "../_helpers/options-with-defaults";
import { PaginatedOptions, UseQueryOptionsNoKeyAndFn } from "../_types";

export const MODEL_GENERATION_SEARCH_RESOURCE = "model-generation" as const;
export const MODEL_GENERATION_SEARCH_OPERATION = "search" as const;
export const MODEL_GENERATION_SEARCH_API_PATH =
  `/v1/${MODEL_GENERATION_SEARCH_RESOURCE}/${MODEL_GENERATION_SEARCH_OPERATION}` as const;

export type ModelGenerationSearchParameters =
  paths[typeof MODEL_GENERATION_SEARCH_API_PATH]["post"]["parameters"]["query"];
export type ModelGenerationSearchBody =
  paths[typeof MODEL_GENERATION_SEARCH_API_PATH]["post"]["requestBody"]["content"]["application/json"];
export type ModelGenerationSearchResponse =
  paths[typeof MODEL_GENERATION_SEARCH_API_PATH]["post"]["responses"][200]["content"]["application/json"];

export function useModelGenerationSearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: PaginatedOptions<ModelGenerationSearchParameters, ModelGenerationSearchBody>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<
    ModelGenerationSearchResponse,
    Error,
    ModelGenerationSearchResponse,
    readonly unknown[]
  >;
} = {}) {
  const optionsWithDefaults =
    optionsWithDefaultsPaginated<PaginatedOptions<ModelGenerationSearchParameters, ModelGenerationSearchBody>>(options);

  const requestParameters = {
    ...(optionsWithDefaults.parameters || {}),
    ...handlePaginatedParameters(optionsWithDefaults.parameters),
  };

  return useQuery({
    queryKey: [
      MODEL_GENERATION_SEARCH_RESOURCE,
      MODEL_GENERATION_SEARCH_OPERATION,
      { parameters: requestParameters, body: optionsWithDefaults.body },
    ],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<ModelGenerationSearchResponse>(
        MODEL_GENERATION_SEARCH_API_PATH,
        optionsWithDefaults.body,
        {
          params: requestParameters,
        },
      );

      return response.data;
    },
    ...queryOptions,
    enabled,
  });
}
